import { useCallback, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';

export enum JoinErrorEnum {
  INACTIVE_WORKSPACE = 'inactive_workspace',
  TOO_MANY_MEMBERS = 'too_many_members',
  INACTIVE_JOIN_PAGE = 'inactive_join_page',
  WORKSPACE_INVITE_NOT_FOUND = 'workspace_pending_membership_not_found',
  INVITE_NOT_FOUND = 'invite_not_found',

  WRONG_EMAIL_INVITED = 'user_email_invited',
  USER_HAS_OTHER_SUBSCRIPTION = 'user_has_other_subscription',
  USER_IS_TEAM_OWNER = 'user_is_team_owner',
  USER_IS_TEAM_MEMBER = 'user_is_team_member',
  USER_IS_ENTERPRISE_MEMBER = 'user_is_enterprise_member',
  USER_HAS_SSO = 'saml_subscription_member',

  WORKSPACE_NOT_FOUND = 'workspace_not_found',
  DEFAULT = 'default_error_message',
}

export enum ErrorImage {
  MOON = 'moon',
  MAGNIFYING = 'magnifying',
  LETTER = 'letter',
  HANDSHAKE = 'handshake',
  PUZZLE = 'puzzle',
  CAT = 'cat',
}

export interface ErrorMessageT {
  title: string;
  main: ReactNode;
  image: ErrorImage;
  ctaButton: { support: boolean; manageMembers?: boolean };
  code: JoinErrorEnum;
  trackingDropoutReason: string;
  isMembershipError?: boolean;
  formError?: string;
}

interface ErrorT {
  error: JoinErrorEnum;
  workspaceName: string;
}

export const useGetErrorMessage = () => {
  const t = useTranslations('onboarding.join_workspace');

  return useCallback(
    ({ error, workspaceName }: ErrorT): ErrorMessageT => {
      const errorMessages: Record<JoinErrorEnum, ErrorMessageT> = {
        [JoinErrorEnum.INACTIVE_JOIN_PAGE]: {
          title: t('inactive_join_page_title'),
          main: t('inactive_join_page_main'),
          image: ErrorImage.MOON,
          ctaButton: { support: true },
          code: JoinErrorEnum.INACTIVE_JOIN_PAGE,
          trackingDropoutReason: 'join page inactive',
        },
        [JoinErrorEnum.INACTIVE_WORKSPACE]: {
          title: t('inactive_workspace_title'),
          main: t('inactive_workspace_main'),
          image: ErrorImage.MOON,
          ctaButton: { support: true },
          code: JoinErrorEnum.INACTIVE_WORKSPACE,
          trackingDropoutReason: 'team is not active',
        },
        [JoinErrorEnum.TOO_MANY_MEMBERS]: {
          title: t('too_many_members_title'),
          main: t.rich('too_many_members_main', { workspaceName }),
          image: ErrorImage.CAT,
          ctaButton: { support: true },
          code: JoinErrorEnum.TOO_MANY_MEMBERS,
          trackingDropoutReason: 'team is full',
        },
        [JoinErrorEnum.WORKSPACE_INVITE_NOT_FOUND]: {
          title: t('workspace_invite_not_found_title'),
          main: t('workspace_invite_not_found_main'),
          image: ErrorImage.LETTER,
          ctaButton: { support: true },
          code: JoinErrorEnum.WORKSPACE_INVITE_NOT_FOUND,
          trackingDropoutReason: 'invalid invitation',
        },
        [JoinErrorEnum.INVITE_NOT_FOUND]: {
          title: t('invite_not_found_title'),
          main: t('invite_not_found_main'),
          image: ErrorImage.LETTER,
          ctaButton: { support: true },
          code: JoinErrorEnum.INVITE_NOT_FOUND,
          trackingDropoutReason: 'invalid invitation',
        },
        [JoinErrorEnum.USER_IS_TEAM_OWNER]: {
          title: t('user_is_team_owner_title'),
          main: t('user_is_team_owner_main'),
          image: ErrorImage.HANDSHAKE,
          ctaButton: { support: false, manageMembers: true },
          code: JoinErrorEnum.USER_IS_TEAM_OWNER,
          trackingDropoutReason: 'user is team owner',
          isMembershipError: true,
          formError: t('user_is_team_owner_form_error'),
        },
        [JoinErrorEnum.USER_IS_TEAM_MEMBER]: {
          title: t('user_is_team_member_title'),
          main: t('user_is_team_member_main'),
          image: ErrorImage.HANDSHAKE,
          ctaButton: { support: true },
          code: JoinErrorEnum.USER_IS_TEAM_MEMBER,
          trackingDropoutReason: 'user is team member',
          isMembershipError: true,
          formError: t('user_is_team_member_form_error'),
        },
        [JoinErrorEnum.USER_IS_ENTERPRISE_MEMBER]: {
          title: t('user_is_enterprise_member_title'),
          main: t('user_is_enterprise_member_main'),
          image: ErrorImage.HANDSHAKE,
          ctaButton: { support: true },
          code: JoinErrorEnum.USER_IS_ENTERPRISE_MEMBER,
          trackingDropoutReason: 'user is enterprise member',
          isMembershipError: true,
          formError: t('user_is_enterprise_member_form_error'),
        },
        [JoinErrorEnum.USER_HAS_OTHER_SUBSCRIPTION]: {
          title: t('user_has_other_subscription_title'),
          main: t('user_has_other_subscription_main'),
          image: ErrorImage.HANDSHAKE,
          ctaButton: { support: true },
          code: JoinErrorEnum.USER_HAS_OTHER_SUBSCRIPTION,
          trackingDropoutReason: 'user is on a paid subscription',
          isMembershipError: true,
          formError: t('user_has_other_subscription_form_error'),
        },
        [JoinErrorEnum.USER_HAS_SSO]: {
          title: t('user_has_sso_title'),
          main: t('user_has_sso_main'),
          image: ErrorImage.HANDSHAKE,
          ctaButton: { support: true },
          code: JoinErrorEnum.USER_HAS_SSO,
          trackingDropoutReason: 'user is on SSO workspace',
        },
        [JoinErrorEnum.WORKSPACE_NOT_FOUND]: {
          title: t('workspace_not_found_title'),
          main: t('workspace_not_found_main'),
          image: ErrorImage.MAGNIFYING,
          ctaButton: { support: true },
          code: JoinErrorEnum.WORKSPACE_NOT_FOUND,
          trackingDropoutReason: 'team not found',
        },
        [JoinErrorEnum.WRONG_EMAIL_INVITED]: {
          title: t('wrong_email_invited_title'),
          main: t('wrong_email_invited_main'),
          image: ErrorImage.LETTER,
          ctaButton: { support: true },
          code: JoinErrorEnum.WRONG_EMAIL_INVITED,
          trackingDropoutReason: 'user email does not match invitation',
        },
        [JoinErrorEnum.DEFAULT]: {
          title: t('default_error_title'),
          main: t('default_error_main'),
          image: ErrorImage.MAGNIFYING,
          ctaButton: { support: true },
          code: JoinErrorEnum.DEFAULT,
          trackingDropoutReason: error || 'unknown error',
        },
      };
      return errorMessages[error] ?? errorMessages[JoinErrorEnum.DEFAULT];
    },
    [t],
  );
};
